import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

import {
  createDefinitionRow,
  deleteDefinitionRow,
  updateDefinitionRow,
} from './api';
import { JsonBlock, parseJsonObject } from './shared';
import {
  AssessmentBenchmark,
  AssessmentContent,
  AssessmentInstrument,
  AssessmentNewsScore,
  AssessmentPeriod,
  DefinitionRowKind,
  JsonObject,
} from './types';

type EditorKind = 'instrument' | 'period' | 'benchmark' | 'news';
type EditorState = { kind: EditorKind; row?: any } | null;

type Props = {
  content: AssessmentContent;
  onChanged: () => Promise<void> | void;
};

const CANONICAL_INSTRUMENTS = [
  'US2Y',
  'US10Y',
  'EU10Y',
  'GB10Y',
  'SPY',
  'EURUSD',
  'USDJPY',
  'USO',
  'US10Y ATM Call',
  'SPY ATM Put',
];

const nullableNumber = (value: unknown) =>
  value === '' || value === undefined ? null : value;

const DefinitionEditor: React.FC<Props> = ({ content, onChanged }) => {
  const {
    scenario,
    instruments,
    periods,
    benchmarks,
    news_scores: newsScores,
  } = content;
  const [editor, setEditor] = useState<EditorState>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const openEditor = (kind: EditorKind, row?: any) => {
    setEditor({ kind, row });
    if (kind === 'instrument') {
      form.setFieldsValue(
        row
          ? {
              ...row,
              unit_config: JSON.stringify(row.unit_config || {}, null, 2),
            }
          : { unit_config: '{}' },
      );
    } else if (kind === 'period') {
      form.setFieldsValue(
        row
          ? {
              ...row,
              benchmark_portfolio: JSON.stringify(
                row.benchmark_portfolio || {},
                null,
                2,
              ),
            }
          : {
              index: periods.length
                ? Math.max(...periods.map(item => item.index)) + 1
                : 1,
              benchmark_portfolio: '{}',
            },
      );
    } else if (kind === 'benchmark') {
      form.setFieldsValue(row || {});
    } else {
      form.setFieldsValue(
        row
          ? { ...row, ics: JSON.stringify(row.ics || {}, null, 2) }
          : { ics: '{}', causal_clarity: null },
      );
    }
  };

  const closeEditor = () => {
    setEditor(null);
    form.resetFields();
  };

  const endpointKind = (kind: EditorKind): DefinitionRowKind =>
    kind === 'instrument'
      ? 'instruments'
      : kind === 'period'
        ? 'periods'
        : kind === 'benchmark'
          ? 'benchmarks'
          : 'news-scores';

  const buildPayload = (kind: EditorKind, values: any): JsonObject => {
    if (kind === 'instrument') {
      const payload: JsonObject = {
        code: values.code,
        underlying_asset: nullableNumber(values.underlying_asset),
        unit_config: parseJsonObject(values.unit_config, 'Unit configuration'),
      };
      [
        'soft_limit_notional',
        'soft_limit_dv01',
        'dv01_per_1m',
        'stress_shock',
        'premium_cap',
      ].forEach(key => {
        if (values[key] !== undefined)
          payload[key] = nullableNumber(values[key]);
      });
      if (!editor?.row) {
        Object.keys(payload).forEach(key => {
          if (payload[key] === null && key !== 'underlying_asset')
            delete payload[key];
        });
      }
      return payload;
    }
    if (kind === 'period') {
      return {
        index: values.index,
        label: values.label,
        start_date: values.start_date,
        end_date: values.end_date,
        regime_label: values.regime_label || '',
        benchmark_portfolio: parseJsonObject(
          values.benchmark_portfolio,
          'Benchmark portfolio',
        ),
      };
    }
    if (kind === 'benchmark') {
      return {
        period: values.period,
        instrument: values.instrument,
        pcs: nullableNumber(values.pcs),
        agreement: nullableNumber(values.agreement),
        open_value: nullableNumber(values.open_value),
        close_value: nullableNumber(values.close_value),
        realized_move: nullableNumber(values.realized_move),
        optimal_target: nullableNumber(values.optimal_target),
        optimal_util: nullableNumber(values.optimal_util),
      };
    }
    return {
      period: values.period,
      news: nullableNumber(values.news),
      news_code: values.news_code,
      headline: values.headline,
      timestamp: values.timestamp || null,
      primary_theme: values.primary_theme || '',
      causal_encoding: values.causal_encoding || '',
      causal_clarity: nullableNumber(values.causal_clarity),
      ics: parseJsonObject(values.ics, 'ICS'),
    };
  };

  const saveRow = async () => {
    if (!editor) return;
    try {
      const values = await form.validateFields();
      const payload = buildPayload(editor.kind, values);
      setSaving(true);
      const kind = endpointKind(editor.kind);
      if (editor.row) {
        await updateDefinitionRow(kind, editor.row.id, scenario.code, payload);
      } else {
        await createDefinitionRow(kind, scenario.code, payload);
      }
      message.success(`${editor.row ? 'Updated' : 'Added'} definition row.`);
      closeEditor();
      await onChanged();
    } catch (requestError) {
      if ((requestError as any)?.errorFields) return;
      message.error((requestError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const removeRow = async (kind: DefinitionRowKind, id: number) => {
    try {
      await deleteDefinitionRow(kind, id, scenario.code);
      message.success('Deleted definition row.');
      await onChanged();
    } catch (requestError) {
      message.error((requestError as Error).message);
    }
  };

  const actions = (kind: EditorKind, row: any, cascade = false) => (
    <Space>
      <Button
        size="small"
        icon={<EditOutlined />}
        disabled={!scenario.editable}
        onClick={() => openEditor(kind, row)}
      >
        Edit
      </Button>
      <Popconfirm
        title="Delete this definition row?"
        description={
          cascade
            ? 'Dependent benchmark rows will also be deleted by the existing database relationship.'
            : 'This change only applies to this unlinked draft.'
        }
        disabled={!scenario.editable}
        onConfirm={() => removeRow(endpointKind(kind), row.id)}
      >
        <Button
          size="small"
          danger
          disabled={!scenario.editable}
          icon={<DeleteOutlined />}
        />
      </Popconfirm>
    </Space>
  );

  const addButton = (kind: EditorKind, label: string, disabled = false) => (
    <Button
      type="primary"
      size="small"
      icon={<PlusOutlined />}
      disabled={!scenario.editable || disabled}
      onClick={() => openEditor(kind)}
    >
      {label}
    </Button>
  );

  const instrumentColumns: any[] = [
    { title: 'Code', dataIndex: 'code', fixed: 'left', width: 150 },
    { title: 'Kind', dataIndex: 'kind_label', width: 90 },
    { title: 'Parent', dataIndex: 'parent_code', width: 110 },
    {
      title: 'PM underlying',
      width: 170,
      render: (_: unknown, row: AssessmentInstrument) =>
        row.underlying_asset ? (
          <span>
            {row.underlying_asset_ticker || 'Asset'}{' '}
            <Typography.Text type="secondary">
              #{row.underlying_asset}
            </Typography.Text>
          </span>
        ) : (
          <Tag color="orange">UNMAPPED</Tag>
        ),
    },
    { title: 'Soft notional', dataIndex: 'soft_limit_notional', width: 120 },
    { title: 'Soft DV01', dataIndex: 'soft_limit_dv01', width: 105 },
    { title: 'DV01 / $1m', dataIndex: 'dv01_per_1m', width: 105 },
    { title: 'Stress shock', dataIndex: 'stress_shock', width: 110 },
    { title: 'Premium cap', dataIndex: 'premium_cap', width: 110 },
    {
      title: 'Unit config',
      dataIndex: 'unit_config',
      width: 230,
      render: (value: JsonObject) => <JsonBlock compact value={value} />,
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 150,
      render: (_: unknown, row: AssessmentInstrument) =>
        actions('instrument', row, true),
    },
  ];

  const periodColumns: any[] = [
    { title: '#', dataIndex: 'index', width: 65 },
    { title: 'Label', dataIndex: 'label', width: 100 },
    { title: 'Start', dataIndex: 'start_date', width: 115 },
    { title: 'End (exclusive)', dataIndex: 'end_date', width: 125 },
    { title: 'Regime', dataIndex: 'regime_label', width: 220 },
    {
      title: 'Portfolio benchmark',
      dataIndex: 'benchmark_portfolio',
      width: 300,
      render: (value: JsonObject) => <JsonBlock compact value={value} />,
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 150,
      render: (_: unknown, row: AssessmentPeriod) =>
        actions('period', row, true),
    },
  ];

  const benchmarkColumns: any[] = [
    { title: 'Period', dataIndex: 'period_label', width: 100 },
    { title: 'Instrument', dataIndex: 'instrument_code', width: 150 },
    { title: 'PCS', dataIndex: 'pcs', width: 90 },
    { title: 'Agreement', dataIndex: 'agreement', width: 100 },
    { title: 'Open', dataIndex: 'open_value', width: 90 },
    { title: 'Close', dataIndex: 'close_value', width: 90 },
    { title: 'Realized move', dataIndex: 'realized_move', width: 115 },
    { title: 'Optimal target', dataIndex: 'optimal_target', width: 115 },
    { title: 'Optimal util.', dataIndex: 'optimal_util', width: 105 },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 150,
      render: (_: unknown, row: AssessmentBenchmark) =>
        actions('benchmark', row),
    },
  ];

  const newsColumns: any[] = [
    { title: 'Period', dataIndex: 'period_label', width: 90 },
    { title: 'Code', dataIndex: 'news_code', width: 110 },
    { title: 'Headline', dataIndex: 'headline', width: 300 },
    {
      title: 'PM news',
      width: 140,
      render: (_: unknown, row: AssessmentNewsScore) =>
        row.news ? (
          <Tag color="green">NEWS #{row.news}</Tag>
        ) : (
          <Tag color="orange">UNLINKED</Tag>
        ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 180,
      className: 'assessment-cell-nowrap',
    },
    { title: 'Theme', dataIndex: 'primary_theme', width: 130 },
    { title: 'Encoding', dataIndex: 'causal_encoding', width: 240 },
    { title: 'Clarity', dataIndex: 'causal_clarity', width: 80 },
    {
      title: 'ICS',
      dataIndex: 'ics',
      width: 260,
      render: (value: JsonObject) => <JsonBlock compact value={value} />,
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 150,
      render: (_: unknown, row: AssessmentNewsScore) => actions('news', row),
    },
  ];

  const table = (
    rows: any[],
    columns: any[],
    kind: EditorKind,
    label: string,
    disabled = false,
  ) => (
    <div>
      <div className="assessment-table-toolbar">
        <Typography.Text type="secondary">
          {scenario.editable
            ? 'Writes are limited to this unlinked draft.'
            : 'Read-only: active and linked definitions cannot be edited.'}
        </Typography.Text>
        {addButton(kind, label, disabled)}
      </div>
      <Table
        rowKey="id"
        size="small"
        className="assessment-table"
        tableLayout="fixed"
        dataSource={rows}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (total: number, column: any) => total + (column.width || 120),
            0,
          ),
        }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </div>
  );

  return (
    <>
      {!scenario.editable && (
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Immutable definition"
          description="View and export remain available. To make changes, create a definition-only clone and work on that new draft."
        />
      )}
      <Tabs
        items={[
          {
            key: 'instruments',
            label: `Instruments (${instruments.length})`,
            children: table(
              instruments,
              instrumentColumns,
              'instrument',
              'Add instrument',
            ),
          },
          {
            key: 'periods',
            label: `Periods (${periods.length})`,
            children: table(periods, periodColumns, 'period', 'Add period'),
          },
          {
            key: 'benchmarks',
            label: `Benchmarks (${benchmarks.length})`,
            children: table(
              benchmarks,
              benchmarkColumns,
              'benchmark',
              'Add benchmark',
              !periods.length || !instruments.length,
            ),
          },
          {
            key: 'news',
            label: `News scores (${newsScores.length})`,
            children: table(
              newsScores,
              newsColumns,
              'news',
              'Add news score',
              !periods.length,
            ),
          },
        ]}
      />

      <Modal
        title={`${editor?.row ? 'Edit' : 'Add'} ${editor?.kind || ''}`}
        open={Boolean(editor)}
        onCancel={closeEditor}
        onOk={saveRow}
        confirmLoading={saving}
        okText={editor?.row ? 'Save changes' : 'Add row'}
        width={760}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          {editor?.kind === 'instrument' && (
            <>
              <Alert
                className="assessment-callout"
                type="info"
                showIcon
                title="Canonical engine instrument"
                description="On creation, omitted limits are seeded by the backend from the same constants used by the existing assessment importer and engine. Code, kind, and parent are immutable."
              />
              <Form.Item
                name="code"
                label="Instrument code"
                rules={[{ required: true }]}
              >
                <Select
                  disabled={Boolean(editor.row)}
                  options={CANONICAL_INSTRUMENTS.map(code => ({
                    value: code,
                    label: code,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="underlying_asset"
                label="PM underlying asset ID (optional)"
                tooltip="The backend validates that this exact PM asset exists. No fuzzy ticker matching is performed."
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
              <div className="assessment-form-grid">
                <Form.Item
                  name="soft_limit_notional"
                  label="Soft limit notional"
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="soft_limit_dv01" label="Soft limit DV01">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="dv01_per_1m" label="DV01 per $1m">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="stress_shock" label="Stress shock">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="premium_cap" label="Premium cap">
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </div>
              <Form.Item
                name="unit_config"
                label="Unit configuration (JSON object)"
              >
                <Input.TextArea
                  autoSize={{ minRows: 4, maxRows: 12 }}
                  spellCheck={false}
                />
              </Form.Item>
            </>
          )}

          {editor?.kind === 'period' && (
            <>
              <div className="assessment-form-grid">
                <Form.Item
                  name="index"
                  label="Period index"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  name="label"
                  label="Label"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="P01" />
                </Form.Item>
                <Form.Item
                  name="start_date"
                  label="Start date"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
                <Form.Item
                  name="end_date"
                  label="End date (exclusive)"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
              </div>
              <Form.Item name="regime_label" label="Regime label">
                <Input />
              </Form.Item>
              <Form.Item
                name="benchmark_portfolio"
                label="Portfolio benchmark (JSON object)"
              >
                <Input.TextArea
                  autoSize={{ minRows: 5, maxRows: 14 }}
                  spellCheck={false}
                />
              </Form.Item>
            </>
          )}

          {editor?.kind === 'benchmark' && (
            <>
              <div className="assessment-form-grid">
                <Form.Item
                  name="period"
                  label="Period"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={periods.map(period => ({
                      value: period.id,
                      label: `${period.index}: ${period.label}`,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="instrument"
                  label="Instrument"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={instruments.map(instrument => ({
                      value: instrument.id,
                      label: instrument.code,
                    }))}
                  />
                </Form.Item>
              </div>
              <div className="assessment-form-grid assessment-form-grid-three">
                {[
                  ['pcs', 'PCS'],
                  ['agreement', 'Agreement'],
                  ['open_value', 'Open value'],
                  ['close_value', 'Close value'],
                  ['realized_move', 'Realized move'],
                  ['optimal_target', 'Optimal target'],
                  ['optimal_util', 'Optimal utilization'],
                ].map(([name, label]) => (
                  <Form.Item key={name} name={name} label={label}>
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                ))}
              </div>
            </>
          )}

          {editor?.kind === 'news' && (
            <>
              <div className="assessment-form-grid">
                <Form.Item
                  name="period"
                  label="Period"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={periods.map(period => ({
                      value: period.id,
                      label: `${period.index}: ${period.label}`,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="news"
                  label={
                    <Tooltip title="Exact PM News ID. Leave empty if no canonical News object is known.">
                      PM news ID (optional)
                    </Tooltip>
                  }
                >
                  <InputNumber
                    min={1}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  name="news_code"
                  label="News code"
                  rules={[{ required: true }]}
                >
                  <Input placeholder="P01-N01" />
                </Form.Item>
                <Form.Item name="timestamp" label="Timestamp (ISO, optional)">
                  <Input placeholder="2021-10-01T12:00:00Z" />
                </Form.Item>
              </div>
              <Form.Item
                name="headline"
                label="Headline"
                rules={[{ required: true }]}
              >
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
              <div className="assessment-form-grid">
                <Form.Item name="primary_theme" label="Primary theme">
                  <Input />
                </Form.Item>
                <Form.Item name="causal_clarity" label="Causal clarity (1–5)">
                  <InputNumber
                    min={1}
                    max={5}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </div>
              <Form.Item name="causal_encoding" label="Causal encoding">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
              <Form.Item
                name="ics"
                label="Instrument conviction scores (JSON object)"
              >
                <Input.TextArea
                  autoSize={{ minRows: 6, maxRows: 16 }}
                  spellCheck={false}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default DefinitionEditor;
